import React, { useState } from "react";
import { List, ListItem } from "@material-ui/core";
import { useSelector } from "react-redux";
import ExitToAppRoundedIcon from "@material-ui/icons/ExitToAppRounded";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { RootState } from "../../store";
import { SiteDetails } from "../User/userSlice";
import OptimusLogoDark from "./assets/OptimusLogoDark";
import LoadingSiteSelector from "./components/LoadingSiteSelector";
import SiteSelector from "./components/SiteSelector";
import { auth } from "../common/utils/auth";
import OverviewIcon from "./assets/OverviewIcon";
import {
    SPACING_1,
    SPACING_4,
    SPACING_8,
    SPACING_12,
    SPACING_20,
    SPACING_28,
    SPACING_80,
} from "../common/styles/spacing";
import {
    GRAY_320, GRAY_500, GRAY_900, WHITE,
} from "../common/styles/colors";
import { HEADING_FONT, FONT_0_625, FONT_1 } from "../common/styles/fonts";

const StyledSidebar = styled(List)`
    width: ${SPACING_80};
    padding-top: 0;
`;

const StyledListItem = styled(ListItem)`
    display: flex;
    flex-direction: column;
    padding-bottom: ${SPACING_4};
    padding-top: ${SPACING_12};
    border-bottom: solid ${SPACING_1} ${GRAY_320};

    transition: background 200ms ease-in;

    svg {
        height: ${SPACING_28};
        width: ${SPACING_28};
    }

    svg.MuiSvgIcon-root {
        height: ${SPACING_20};
        width: ${SPACING_20};
        transform: scaleX(-1);
    }

    &.Mui-selected {
        background: ${(props) => (props.selected ? GRAY_900 : "transparent")};

        &:hover {
            background: ${(props) => (props.selected ? GRAY_900 : "transparent")};
        }
    }
`;

const StyledSidebarTitle = styled.span`
    display: flex;
    align-items: center;

    font-family: ${HEADING_FONT};
    color: ${(props: { selected: boolean }) => (props.selected ? WHITE : GRAY_500)};
    font-size: ${FONT_0_625};
    font-weight: semi-bold;

    text-rendering: optimizelegibility;
    text-transform: uppercase;

    margin-top: ${SPACING_12};

    padding: ${SPACING_8};
`;

const StyledDecorator = styled.span`
    font-size: ${FONT_1};
    padding: ${SPACING_1} 0 ${SPACING_4};
    margin-left: ${SPACING_4};
`;

const handleLogout = () => auth.logout();
const goToCommandor = () => window.open("__COMMANDOR_URL__", "_blank");

export default ({ className }: { className?: string }) => {
    const { siteOptions, selectedSite } = useSelector<RootState, SiteDetails>(
        (state) => state.user.siteDetails,
    );
    const history = useHistory();
    const { t } = useTranslation();
    const [siteSelectorOpen, setSiteSelectorOpen] = useState(false);

    const toggleSiteSelector = () => setSiteSelectorOpen((open) => !open);
    const handleSiteSelect = (initials: string) => {
        setSiteSelectorOpen(false);

        // since the site selector only changes a query param,
        // we grab the current url, remove the site query param and
        // add the new one on it and update the history
        const url = new URL(window.location.toString());
        url.searchParams.delete("site");
        url.searchParams.append("site", initials);

        history.push(`${url.pathname}?${url.searchParams.toString()}`);
    };

    const SIDEBAR_VALUES = [
        {
            title: t("sidebar.title.site"),
            decorator: <StyledDecorator>▸</StyledDecorator>,
            component: selectedSite ? (
                <SiteSelector
                    siteOptions={siteOptions}
                    selectedSite={selectedSite}
                    onSelect={handleSiteSelect}
                    open={siteSelectorOpen}
                />
            ) : (
                <LoadingSiteSelector />
            ),
            onClick: toggleSiteSelector,
        },
        {
            title: t("sidebar.title.overview"),
            selected: true,
            component: <OverviewIcon />,
        },
        {
            title: t("sidebar.title.commandOR"),
            component: (
                <OptimusLogoDark />
            ),
            onClick: goToCommandor,
        },
        {
            title: t("sidebar.title.logout"),
            component: <ExitToAppRoundedIcon />,
            onClick: handleLogout,
        },
    ];

    return (
        <StyledSidebar className={className}>
            {SIDEBAR_VALUES.map(({
                title, component, selected, onClick, decorator,
            }) => (
                <StyledListItem
                    button
                    key={title}
                    selected={!!selected}
                    onClickCapture={onClick}
                >
                    {component}
                    <StyledSidebarTitle selected={!!selected}>
                        {title}
                        {" "}
                        {decorator}
                    </StyledSidebarTitle>
                </StyledListItem>
            ))}
        </StyledSidebar>
    );
};
